import { createAction, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import hashSum from 'hash-sum'

import * as atypes from 'apps/chat/constants/LiveChatReduxActionTypes'
import { LiveChatThread } from 'apps/chat/constants/LiveChatThreads'
import { ThreadStatus } from 'apps/chat/interfaces/statuses'
import { ThreadsSocketData } from 'apps/chat/interfaces/threadsSocketSchema'
import {
  getFolder,
  getFolderUserId,
  getThreadsFilterData,
  getThreadsSorting,
  getThreadsStatusFilter,
} from 'apps/chat/selectors/threadSelectors'
import { ChannelType } from 'common/core/constants/ChannelType'
import { FilterOperator } from 'common/filter/filterConstants'
import { Filter, FilterCondition, FilterGroupItem } from 'common/filter/interfaces'
import { PreloadImagesArray } from 'components/RichTextEditor/processor/constants'
import { mapResponse } from 'shared/api/lib/mapResponse'
import { chatApi } from 'shared/api/requests/chat'
import { chatApiGuard, HopError, ThreadControlError } from 'shared/api/requests/chat/guards'
import {
  ThreadsFetchRequestBody,
  ThreadsFetchResponse,
} from 'shared/api/requests/chat/threads/schemas'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'
import { createAsyncAction } from 'shared/lib/redux/createAsyncAction'
import { navigatePollyfill } from 'utils/router/tools'
import { linkURL } from 'utils/url'

import { PendingVoiceConversionState } from '../../actions/interfaces'
import {
  ALL_TEAM_MEMBERS_ID,
  ConversationFilterType,
  DEFAULT_THREAD_LIMIT,
  SelectionType,
  StatusFilterType,
  ThreadControlErrorType,
  ThreadsSortingTypes,
} from '../../constants/LiveChatInterfaces'
import {
  ConversationWindowType,
  CurrentThread,
  LoadingMessagesThreads,
  UploadedImagesThreadsState,
} from '../../models/chatInterfaces'

export const NAMESPACE = 'threads'

interface ThreadState {
  assignee: number | null
  channel: ChannelType
  checkedFilterIds: Record<string, { loading: boolean; satisfy?: boolean }>
  current: CurrentThread
  currentId: string | null
  fetchingPauseIds: Record<string, boolean>
  filter: Filter<FilterGroupItem> | Filter<FilterCondition>
  isHistoryModal: boolean
  selectedThreads: Record<string, LiveChatThread>
  selectionType: SelectionType
  statusFilter: StatusFilterType
  threadsSorting: ThreadsSortingTypes
  totalThreadsCount: number | null
  uploadedImagesThreads: UploadedImagesThreadsState
  loadingMessagesThreads: LoadingMessagesThreads
  conversationsFilter: ConversationFilterType
  channelsFilter: ChannelType | null
  teamMembersFilter: number
  pendingVoiceConversionStates: PendingVoiceConversionState[]
}

export const threadState: ThreadState = {
  assignee: null,
  channel: ChannelType.FB,
  checkedFilterIds: {},
  current: {
    item: null,
    fetching: false,
    hash: null,
    hasChanges: false,
    error: null,
    freeEntryPointWindow: {
      isOpen: false,
      isExpired: false,
    },
    customerServiceWindow: {
      isOpen: false,
      isExpired: false,
    },
    threadControlError: null,
  },
  currentId: null,
  fetchingPauseIds: {},
  filter: {
    operator: FilterOperator.AND,
    groups: [],
  },
  isHistoryModal: false,
  selectedThreads: {},
  selectionType: SelectionType.INCLUDE,
  statusFilter: StatusFilterType.OPEN,
  threadsSorting: ThreadsSortingTypes.NEWEST,
  totalThreadsCount: null,
  conversationsFilter: ConversationFilterType.ALL,
  pendingVoiceConversionStates: [],
  channelsFilter: null,
  teamMembersFilter: ALL_TEAM_MEMBERS_ID,
  uploadedImagesThreads: {},
  loadingMessagesThreads: {},
}

const {
  reducer: threadReducer,
  actions,
  selectors: threadSelectors,
  initialState: threadInitialState,
} = createListSlice({
  namespace: NAMESPACE,

  storePath: `chat.${NAMESPACE}`,

  listKey: NAMESPACE,

  operations: createListOperations({
    getList: mapResponse(chatApi.threads.fetch, (response) => response.data),
    getNextList: mapResponse(chatApi.threads.fetch, (response) => response.data),
    getPrevList: undefined,
    createItem: undefined,
    updateItem: undefined,
    deleteItem: undefined,
  }),

  getItemId: (thread: LiveChatThread) => thread.user_id,

  extraState: threadState,

  reducers: {
    updatedCurrentItemNotification(
      state,
      action: PayloadAction<ThreadsSocketData | LiveChatThread>,
    ) {
      const updatedItem = action.payload
      if (state.current.item == null || state.current.item.user_id !== updatedItem.user_id) {
        return
      }

      const hash = hashSum(updatedItem)
      const item = { ...state.current.item, ...updatedItem }
      state.current.hasChanges = hash !== hashSum(item)
      state.current.hash = hash
      state.current.item = item
    },
    setCurrentThread: (state, action: PayloadAction<string | null>) => {
      state.currentId = action.payload
    },
    updateStatusFilter: (state, action: PayloadAction<StatusFilterType>) => {
      state.statusFilter = action.payload
    },
    updateTagsAndCufsFilter: (
      state,
      action: PayloadAction<Filter<FilterGroupItem> | Filter<FilterCondition>>,
    ) => {
      state.filter = action.payload
    },
    updateConversationsFilter: (state, action: PayloadAction<ConversationFilterType>) => {
      state.conversationsFilter = action.payload
    },
    updateTeamMembersFilter: (state, action: PayloadAction<number>) => {
      state.teamMembersFilter = action.payload
    },
    updateChannelsFilter: (state, action: PayloadAction<ChannelType | null>) => {
      state.channelsFilter = action.payload
    },
    updateThreadsSorting: (state, action: PayloadAction<ThreadsSortingTypes>) => {
      state.threadsSorting = action.payload
    },
    setConversationWindowStatus: (
      state,
      action: PayloadAction<{
        windowType: ConversationWindowType
        isOpen: boolean
        isExpired: boolean
      }>,
    ) => {
      const { windowType, isOpen, isExpired } = action.payload
      if (windowType === ConversationWindowType.FREE_ENTRY_POINT) {
        state.current.freeEntryPointWindow = { isOpen, isExpired }
      } else {
        state.current.customerServiceWindow = { isOpen, isExpired }
      }
    },
    increaseTotalThreadsCount: (state) => {
      state.totalThreadsCount = Number(state.totalThreadsCount) + 1
    },
    decreaseTotalThreadsCount: (state) => {
      state.totalThreadsCount = Number(state.totalThreadsCount) - 1
    },
    updateThreadSubscriberInfo: (
      state,
      action: PayloadAction<{ threadId: string; user: LiveChatThread['user'] }>,
    ) => {
      const { threadId, user } = action.payload

      if (state.current?.item && threadId === state.current.item?.id) {
        state.current.item.user = user
      }
    },
    toggleChecked: (state, action: PayloadAction<LiveChatThread>) => {
      const { user_id: id } = action.payload

      const selectedThreads = { ...state.selectedThreads }

      if (selectedThreads[id]) {
        delete selectedThreads[id]
      } else {
        selectedThreads[id] = action.payload
      }

      state.selectedThreads = selectedThreads
    },
    toggleBulkSelection: (state) => {
      const hasExcludedType = state.selectionType === SelectionType.EXCLUDE
      const newSelectionType = hasExcludedType ? SelectionType.INCLUDE : SelectionType.EXCLUDE

      state.selectionType = newSelectionType
      state.selectedThreads = {}
    },
    updateSelectionType: (state, action: PayloadAction<SelectionType>) => {
      state.selectionType = action.payload
    },
    clearSelection: (state) => {
      state.selectedThreads = {}
      state.selectionType = SelectionType.INCLUDE
    },
    setAssignee: (state, action: PayloadAction<number | null>) => {
      state.assignee = action.payload
    },
    processSendMessageError: (
      state,
      action: PayloadAction<Partial<{ error: HopError | ThreadControlError; messageId: string }>>,
    ) => {
      if (!action.payload?.error) {
        return
      }

      const error = action.payload.error

      if (chatApiGuard.isThreadControlError(error)) {
        state.current.threadControlError = error.error_data.thread_control_error
      } else if (chatApiGuard.isHopError(error)) {
        state.current.threadControlError = error.error_data.hop_error
      } else {
        state.current.threadControlError = null
      }
    },
    addVoiceConversionPendingState: (state, action: PayloadAction<PendingVoiceConversionState>) => {
      state.pendingVoiceConversionStates.push(action.payload)
    },
    removeVoiceConversionPendingState: (state, action: PayloadAction<number>) => {
      state.pendingVoiceConversionStates = state.pendingVoiceConversionStates.filter(
        ({ message }) => message?.attachment_id?.[0] !== action.payload,
      )
    },
    switchLiveChatChannel: (state, action: PayloadAction<ChannelType>) => {
      state.channel = action.payload
    },
    resetThreadLoading: (state) => {
      state.loadingMessagesThreads = {}
    },
    addPreloadImage: (state, action: PayloadAction<PreloadImagesArray>) => {
      const { currentId, channel } = state
      const data = action.payload

      if (!currentId) return

      if (!state.uploadedImagesThreads?.[currentId]) {
        state.uploadedImagesThreads[currentId] = {}
      }

      const existingImages = state.uploadedImagesThreads[currentId]?.[channel]

      if (existingImages) {
        existingImages.push(...data)
      } else {
        state.uploadedImagesThreads[currentId][channel] = data
      }
    },
    deletePreloadImage: (state, action: PayloadAction<number>) => {
      const index = action.payload
      const { currentId, channel } = state

      if (!currentId) return

      const arrImg = state.uploadedImagesThreads?.[currentId]?.[channel]
      const newArrImg = [...(arrImg || []).slice(0, index), ...(arrImg || []).slice(index + 1)]

      state.uploadedImagesThreads[currentId][channel] = newArrImg
    },
    resetImages: (state, action: PayloadAction<{ currentId: string; channel: ChannelType }>) => {
      const { currentId, channel } = action.payload
      delete state.uploadedImagesThreads[currentId][channel]
    },
    resetConversationRoutingError: (state) => {
      state.current.threadControlError = null
    },
    setThreadsRequest: (
      state,
      action: PayloadAction<{ channel: ChannelType; messageId: string }>,
    ) => {
      const { currentId } = state
      const { messageId, channel } = action.payload

      if (!currentId) return

      if (!state.loadingMessagesThreads?.[currentId]) {
        state.loadingMessagesThreads[currentId] = {}
      }

      const existingLoadingMessages = state.loadingMessagesThreads[currentId]?.[channel]

      if (existingLoadingMessages) {
        existingLoadingMessages.push(messageId)
      } else {
        state.loadingMessagesThreads[currentId][channel] = [messageId]
      }
    },
    setThreadsError: (
      state,
      action: PayloadAction<{
        currentId: string
        channel: ChannelType
        error: CurrentThread['error']
      }>,
    ) => {
      const { currentId, channel, error } = action.payload

      if (state.loadingMessagesThreads[currentId]) {
        state.loadingMessagesThreads[currentId][channel] = undefined
      }

      state.current.error = error
    },
    setThreadsResponse: (
      state,
      action: PayloadAction<{
        currentId: string
        channel: ChannelType
        messageId: string
      }>,
    ) => {
      const { currentId, channel, messageId } = action.payload
      const currentThread = state.loadingMessagesThreads[currentId]?.[channel]

      if (!currentThread) return

      state.loadingMessagesThreads[currentId][channel] = currentThread.filter(
        (msgId) => msgId !== messageId,
      )
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchThread.pending, (state) => {
      state.current.fetching = true
      state.current.error = null
    })
    builder.addCase(fetchThread.rejected, (state, action) => {
      state.current.fetching = false
      state.current.error = {
        errors: [action.error?.message || 'Unknown error'],
      }
    })
    builder.addCase(fetchThread.fulfilled, (state, action) => {
      const item = action.payload

      state.current = {
        ...state.current,
        fetching: false,
        hash: hashSum(item),
        hasChanges: false,
        threadControlError: item.hop_error?.error_data?.hop_error || null,
        item: {
          ...item.thread,
          id: item.thread.user_id,
          notes: item.notes,
        },
      }
    })
    builder.addCase(dropThreadsList.fulfilled, (state) => {
      state.selectedThreads = {}
      state.selectionType = SelectionType.INCLUDE
      state.totalThreadsCount = null
    })
    builder.addCase(checkIsThreadSatisfiesCurrentFilter.pending, (state, action) => {
      const userId = action.meta.arg
      state.checkedFilterIds[userId] = {
        loading: true,
        satisfy: false,
      }
    })
    builder.addCase(checkIsThreadSatisfiesCurrentFilter.fulfilled, (state, action) => {
      const { subscriber_id, satisfy } = action.payload
      state.checkedFilterIds[subscriber_id] = {
        loading: false,
        satisfy,
      }
    })
    builder.addCase(checkIsThreadSatisfiesCurrentFilter.rejected, (state, action) => {
      const userId = action.meta.arg
      state.checkedFilterIds[userId] = {
        loading: false,
        satisfy: false,
      }
    })
    builder.addMatcher(
      isAnyOf(fetchThreads.pending.match, fetchNextThreads.pending.match),
      (state) => {
        state.fetching = true
      },
    )
    builder.addMatcher(
      isAnyOf(fetchThreads.fulfilled.match, fetchNextThreads.fulfilled.match),
      (state, action) => {
        if (!action.payload) return
        const data = action.payload as ThreadsFetchResponse
        state.totalThreadsCount = data.total_threads_num || state.totalThreadsCount
        state.fetching = false
      },
    )
    builder.addMatcher(
      isAnyOf(fetchThreads.rejected.match, fetchNextThreads.rejected.match),
      (state) => {
        state.fetching = false
      },
    )
    builder.addMatcher(
      isAnyOf(updateThreadStatus.rejected.match, setAssignment.rejected.match),
      (state, action) => {
        state.current.error = {
          errors: [action.error?.message || 'Unknown error'],
        }
      },
    )
    builder.addMatcher(
      isAnyOf(resetHandoverError.match, takeThreadControl.fulfilled.match),
      (state) => {
        const { threadControlError } = state.current

        const isCurrentlyHandoverError =
          threadControlError?.type === ThreadControlErrorType.HANDOVER_PRIMARY_THREAD_OWNERSHIP ||
          threadControlError?.type === ThreadControlErrorType.HANDOVER_SECONDARY_THREAD_OWNERSHIP

        if (isCurrentlyHandoverError) {
          state.current.threadControlError = null
        }
      },
    )
    builder.addMatcher(
      (action) => action.type === atypes.LIVE_CHAT_SHOW_ALL_CHANNELS_HISTORY_MODAL,
      (state) => {
        state.isHistoryModal = true
      },
    )
    builder.addMatcher(
      (action) => action.type === atypes.LIVE_CHAT_CLOSE_ALL_CHANNELS_HISTORY_MODAL,
      (state) => {
        state.isHistoryModal = false
      },
    )
  },
})

const dropThreadsList = createAsyncAction(
  `${NAMESPACE}/dropThreadsList`,
  async (_, { dispatch }) => {
    dispatch(actions.dropList())
  },
)

const fetchThread = createAsyncAction(
  `${NAMESPACE}/fetchThread`,
  async (threadId: string) => {
    const response = await chatApi.threads.fetchThread({ query: { user_id: threadId } })
    return response.data
  },
  {
    autoHandleByGuards: [chatApi.threads.fetchThread.isCurrentThreadUserError],
  },
)

const fetchThreads = createAsyncAction(
  `${NAMESPACE}/fetchThreads`,
  async (_, { dispatch, getState }) => {
    const state = getState()

    const body: ThreadsFetchRequestBody = {
      limit: DEFAULT_THREAD_LIMIT,
      sorting: getThreadsSorting(state),
      filter: getThreadsFilterData(state),
      status: getThreadsStatusFilter(state),
      folder: getFolder(state),
      folder_user_id: getFolderUserId(state),
    }

    const response = await dispatch(actions.getListFx({ payload: { body } })).unwrap()
    return response
  },
)

const fetchNextThreads = createAsyncAction(
  `${NAMESPACE}/fetchNextThreads`,
  async (_, { dispatch }) => {
    const response = await dispatch(actions.getNextListFx()).unwrap()
    return response
  },
)

const takeThreadControl = createAsyncAction(
  `${NAMESPACE}/takeThreadControl`,
  async (threadId: string) => {
    const response = await chatApi.threads.takeThreadControl({ body: { subscriber_id: threadId } })
    return response.data
  },
)

const markThreadAsRead = createAsyncAction(
  `${NAMESPACE}/markThreadAsRead`,
  async (threadId: string, { getState }) => {
    const state = getState()
    const currentThread = state.chat.threads.current.item

    if (!currentThread || currentThread.id !== threadId || currentThread.lc_thread_read) {
      return
    }

    const response = await chatApi.threads.markThreadAsRead({ body: { user_id: threadId } })

    return response.data
  },
  {
    autoHandleByGuards: [chatApi.threads.markThreadAsRead.isCurrentThreadUserError],
  },
)

const markThreadAsUnread = createAsyncAction(
  `${NAMESPACE}/markThreadAsUnread`,
  async (thread: LiveChatThread) => {
    if (!thread || !thread.lc_thread_read || !thread.id) return

    await chatApi.threads.markThreadAsUnread({ body: { user_id: thread.id } })

    navigatePollyfill(linkURL(`/chat`))
  },
  {
    autoHandleByGuards: [chatApi.threads.markThreadAsUnread.isCurrentThreadUserError],
  },
)

const updateThreadStatus = createAsyncAction(
  `${NAMESPACE}/updateThreadStatus`,
  async ({ threadId, status }: { threadId: string; status: ThreadStatus }) => {
    const response = await chatApi.threads.updateThreadStatus({
      body: { user_id: threadId, thread_status: status },
    })

    return response.data
  },
  {
    autoHandleByGuards: [chatApi.threads.updateThreadStatus.isCurrentThreadUserError],
  },
)

const setAssignment = createAsyncAction(
  `${NAMESPACE}/setAssignment`,
  async ({
    assignedUserId,
    threadId,
  }: {
    assignedUserId: string | number | null
    threadId: string
  }) => {
    const response = await chatApi.threads.setAssignment({
      body: {
        assigned_user_id: assignedUserId,
        user_id: threadId,
      },
    })

    return response.data
  },
  {
    autoHandleByGuards: [chatApi.threads.setAssignment.isAssignmentError],
  },
)

const checkIsThreadSatisfiesCurrentFilter = createAsyncAction(
  `${NAMESPACE}/checkIsThreadSatisfiesCurrentFilter`,
  async (user_id: string, { getState }) => {
    const state = getState()
    const filter = getThreadsFilterData(state)

    const { data } = await chatApi.threads.checkThreadSatisfiesFilter({
      body: {
        subscriber_id: user_id,
        filter,
      },
    })

    return {
      subscriber_id: user_id,
      satisfy: data.satisfy,
    }
  },
)

const resetHandoverError = createAction<void>(`${NAMESPACE}/resetHandoverError`)

const fetchThreadPartialDetails = createAsyncAction(
  `${NAMESPACE}/fetchThreadPartialDetails`,
  async (query: { threadId: string; withLabels?: boolean }) => {
    const { threadId, withLabels } = query
    const response = await chatApi.threads.loadThreadPartialDetails({
      query: { subscriber_id: threadId, with_labels: withLabels },
    })
    return response.data
  },
)

const threadActions = {
  ...actions,
  // all other actions from the file
  dropThreadsList,
  fetchThread,
  fetchThreads,
  fetchNextThreads,
  takeThreadControl,
  markThreadAsRead,
  markThreadAsUnread,
  updateThreadStatus,
  setAssignment,
  checkIsThreadSatisfiesCurrentFilter,
  resetHandoverError,
  fetchThreadPartialDetails,
}

export {
  threadReducer,
  threadActions,
  actions as sliceActions,
  threadSelectors,
  threadInitialState,
}
